import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { X } from "lucide-react";
import { z } from "zod";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import RHFSelect from "@/components/form/RHFSelect";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { getCountryList } from "@/utils/countryList";
import CalendarTypeService from "@/services/calendarTypeService";

const countrySchema = z.object({
  country: z.string().min(1, "Country is required"),
});

const COUNTRY_OPTIONS = getCountryList();

// Name uniqueness ignores isActive, so re-picking a country whose calendar
// was disabled always 409s — worth spelling out here instead of a generic
// "already exists", since the fix (re-enable it below) isn't obvious from
// the add flow alone.
const DUPLICATE_MESSAGE = "A calendar for this country already exists. If it was previously disabled, re-enable it below instead of adding it again.";

const AddCalendarTypeModal = ({ isOpen, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const methods = useForm({
    resolver: zodResolver(countrySchema),
    defaultValues: { country: "" },
    mode: "onSubmit",
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (isOpen) {
      reset({ country: "" });
      setInlineError("");
    }
  }, [isOpen, reset]);

  const submitForm = async (values) => {
    try {
      setSubmitting(true);
      setInlineError("");
      await CalendarTypeService.create({ name: `${values.country} Calendar` });
      onSaved?.();
    } catch (err) {
      if (err?.response?.data?.error_code === "DUPLICATE_CALENDAR_TYPE_NAME") {
        setInlineError(DUPLICATE_MESSAGE);
      } else {
        setInlineError(apiErrorMessageFormat(err, "Failed to create calendar."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="flex w-[95vw] max-w-[480px] max-h-[90vh] flex-col overflow-hidden p-0">
        <DialogHeader className="relative shrink-0 border-b border-border-primary px-5 py-3">
          <div className="pr-10">
            <DialogTitle className="text-[15px] font-semibold text-text-primary">Add Calendar</DialogTitle>
            <DialogDescription className="mt-0.5 text-[12px] text-text-primary/70">
              Pick a country to create its calendar. Jobs created after this will be able to select it.
            </DialogDescription>
          </div>
          <DialogClose className="absolute right-5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-background-icon/40">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)} className="flex min-h-0 flex-1 flex-col">
            <div className="custom-scrollbar flex-1 min-h-0 space-y-4 overflow-y-auto p-5">
              <RHFSelect
                name="country"
                label="Country"
                options={COUNTRY_OPTIONS}
                searchable
                searchPlaceholder="Search countries..."
                placeholder="Select a country"
                required
              />

              {inlineError ? (
                <div className="rounded-xl border border-[rgb(var(--color-error))]/20 bg-[rgb(var(--color-error))]/10 px-3 py-2 text-xs font-medium text-[rgb(var(--color-error))]">
                  {inlineError}
                </div>
              ) : null}
            </div>

            <div className="shrink-0 border-t border-border-primary bg-background-secondary px-5 py-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" onClick={onClose} className="w-full sm:w-auto rounded-full px-4 py-2 bg-background-icon text-text-primary">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="w-full sm:w-auto rounded-full px-4 py-2 bg-button-secondary text-text-base disabled:opacity-60">
                  {submitting ? "Adding..." : "Add Calendar"}
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddCalendarTypeModal;
